import React, { useState, useEffect, useCallback } from 'react';
import { cases } from '../../services/api';

/**
 * Grups d'examen amb codi imprès — la pantalla de l'instructor.
 *
 * El dia de l'examen cada grup rep una targeta amb el seu nom i el seu codi, i
 * entra amb això i prou. Aquí es preparen els grups, es veuen els codis i
 * s'imprimeixen les targetes.
 *
 * La impressió és una vista a part (`@media print`): del full que en surt només
 * hi ha les targetes, una per grup, retallables. Cada targeta porta el nom, el
 * codi i **on s'ha d'anar** — sense l'adreça, la targeta no serveix de res.
 *
 * Model copiat d'ACH, que ja ha passat per una aula.
 */
function GrupsExamen({ caseId, caseTitle, onClose }) {
  const [grups, setGrups] = useState([]);
  const [carregant, setCarregant] = useState(true);
  const [error, setError] = useState('');
  const [avis, setAvis] = useState('');
  const [quantitat, setQuantitat] = useState(12);
  const [ocupat, setOcupat] = useState(false);

  const carrega = useCallback(async () => {
    try {
      setCarregant(true);
      const { data } = await cases.getGrups(caseId);
      setGrups(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant els grups');
    } finally {
      setCarregant(false);
    }
  }, [caseId]);

  useEffect(() => { carrega(); }, [carrega]);

  const fes = async (accio, missatge) => {
    try {
      setOcupat(true);
      setError('');
      setAvis('');
      await accio();
      setAvis(missatge);
      await carrega();
      setTimeout(() => setAvis(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'No s\'ha pogut completar l\'operació');
    } finally {
      setOcupat(false);
    }
  };

  const prepara = () => fes(
    () => cases.preparaGrups(caseId, Number(quantitat)),
    `${quantitat} grup(s) preparats per a aquest examen`
  );

  const regenera = (grup) => {
    if (!window.confirm(
      `Donar un codi nou a ${grup.displayName}?\n\nLa targeta actual deixarà de servir a l'instant i, si té una sessió oberta, es tancarà.`
    )) return;
    return fes(() => cases.regeneraCodiGrup(caseId, grup.id), 'Codi regenerat');
  };

  const revoca = (grup) => {
    if (!window.confirm(
      `Treure el codi a ${grup.displayName}?\n\nDeixarà de poder entrar, però el compte i tota la seva feina es queden. Es desfà donant-li un codi nou.`
    )) return;
    return fes(() => cases.revocaCodiGrup(caseId, grup.id), 'Codi revocat');
  };

  const revocaTots = () => {
    if (!window.confirm(
      "Treure el codi a TOTS els grups d'aquest examen?\n\n" +
      "Ningú no podrà tornar a entrar amb la targeta. Les entregues i les notes es queden, però ELS ALUMNES JA NO LES PODRAN CONSULTAR: el codi és la seva única credencial.\n\n" +
      "Fes-ho quan hagis acabat de corregir I ells ja hagin vist el teu comentari, no just en acabar l'examen."
    )) return;
    return fes(() => cases.revocaTotsElsCodis(caseId), 'Codis revocats');
  };

  const ambCodi = grups.filter(g => g.teCodi);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:static print:bg-transparent print:block">
      <div className="bg-surface rounded-lg shadow-modal w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col print:max-w-none print:max-h-none print:shadow-none print:rounded-none print:mx-0">

        {/* Tot això desapareix en imprimir */}
        <div className="print:hidden flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-content">Grups i codis d'examen</h2>
            <p className="text-sm text-content-muted mt-0.5">{caseTitle}</p>
          </div>
          <button onClick={onClose} className="text-content-muted hover:text-content-secondary" aria-label="Tancar">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="print:hidden flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded">{error}</div>
          )}
          {avis && (
            <div className="bg-success-subtle border border-success/30 text-success-text px-4 py-3 rounded">{avis}</div>
          )}

          <div className="bg-surface-subtle rounded-lg p-4">
            <h3 className="font-medium text-content mb-1">Preparar l'examen</h3>
            <p className="text-sm text-content-secondary mb-3">
              Crea els grups que facin falta, els inscriu en aquest cas i dona codi
              als que no en tinguin. <strong>No canvia els codis ja impresos</strong>:
              preparar un examen nou no pot invalidar targetes d'un altre que
              s'estigui fent.
            </p>
            <div className="flex items-end gap-3">
              <div>
                <label htmlFor="quantitat" className="block text-sm text-content-secondary mb-1">Quants grups</label>
                <input id="quantitat" type="number" min="1" max="60" value={quantitat}
                       onChange={(e) => setQuantitat(e.target.value)}
                       className="input w-28" />
              </div>
              <button onClick={prepara} disabled={ocupat} className="btn btn-primary disabled:opacity-50">
                {ocupat ? 'Preparant...' : 'Preparar grups'}
              </button>
            </div>
          </div>

          {carregant ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
            </div>
          ) : grups.length === 0 ? (
            <p className="empty-state-text py-10 text-center">
              Encara no hi ha cap grup en aquest examen. Prepara'n amb el formulari de dalt.
            </p>
          ) : (
            <>
              <div className="bg-accent-subtle border border-accent/30 rounded p-3 text-sm text-content-secondary">
                <strong className="text-content">Després de l'examen:</strong> deixa els
                codis actius fins que hagis corregit i els alumnes hagin vist la nota i el
                teu comentari. Amb el codi revocat no hi poden tornar a entrar, perquè és
                la seva única credencial. L'exercici ja queda en només lectura tot sol en
                enviar-lo.
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-content-secondary">
                  {grups.length} grup(s) · {ambCodi.length} amb codi actiu
                </p>
                <div className="flex gap-2">
                  <button onClick={() => window.print()} disabled={ambCodi.length === 0}
                          className="btn btn-secondary disabled:opacity-50">
                    Imprimir targetes
                  </button>
                  <button onClick={revocaTots} disabled={ocupat || ambCodi.length === 0}
                          className="btn btn-danger disabled:opacity-50"
                          title="Fes-ho quan els alumnes ja hagin vist la teva correcció">
                    Revocar tots els codis
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="table-header text-left">
                      <th className="px-3 py-2">Grup</th>
                      <th className="px-3 py-2">Codi</th>
                      <th className="px-3 py-2">Estat</th>
                      <th className="px-3 py-2 text-right">Accions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grups.map(g => (
                      <tr key={g.id} className="border-b border-border-subtle">
                        <td className="px-3 py-2 font-medium text-content">{g.displayName}</td>
                        <td className="px-3 py-2 font-mono tracking-wider text-content">
                          {g.codi || <span className="text-content-disabled font-sans italic">sense codi</span>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1.5">
                            {g.sessioOberta && <span className="badge badge-info">connectat</span>}
                            {g.assessmentStatus && <span className="badge badge-neutral">{g.assessmentStatus}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => regenera(g)} disabled={ocupat}
                                  className="link text-sm mr-3">Codi nou</button>
                          {g.teCodi && (
                            <button onClick={() => revoca(g)} disabled={ocupat}
                                    className="text-sm text-error hover:text-error-text">Revocar</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/*
          El full de targetes. Amagat a la pantalla i l'única cosa que surt en
          imprimir. Només els grups amb codi: una targeta sense codi no serveix.
        */}
        <div className="hidden print:block">
          <div className="targetes-examen">
            {ambCodi.map(g => (
              <div className="targeta-grup" key={g.id}>
                {/* El nom del cas va a la targeta des del 08/08/2026, quan els
                    grups van passar a ser del cas i no d'un dipòsit global.
                    Ara hi ha un «Grup 01» per cada examen, o sigui que dues
                    piles de targetes de casos diferents es veurien EXACTAMENT
                    igual: sense això, barrejar-les seria fàcil i no es
                    detectaria fins que un equip entrés a l'examen que no toca. */}
                <div className="targeta-grup__cas">{caseTitle}</div>
                <div className="targeta-grup__nom">{g.displayName}</div>
                <div className="targeta-grup__codi">{g.codi}</div>
                <div className="targeta-grup__peu">
                  <div className="targeta-grup__adreca">argos.strategos.cat</div>
                  <div className="targeta-grup__instruccio">
                    Entra amb aquest codi a «Codi d'examen»
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GrupsExamen;
